import { resolveLessonSlug } from 'experienceComponents/Lesson/helpers'
import { resolveCourseNodeEntryPointFromSummary } from 'utils/lms/linkSummaryCompletionHelpers'

const normalizeRoute = route => {
  if (typeof route !== 'string') return ''

  return route
    .trim()
    .replace(/\/+$/g, '')
    .split('/')
    .pop()
}

const getConnectorId = entry => resolveCourseNodeEntryPointFromSummary(entry)

export const resolveLessonDoneState = ({ lesson, lessonSummary }) => {
  if (lessonSummary?.lessonComplete === true) return true

  return Boolean(lesson?.is_done)
}

export const getLessonSummary = ({
  contentfulLesson,
  lessonIndex,
  lessonListIndex,
  lessonSummaries,
}) => {
  if (!Array.isArray(lessonSummaries)) return null

  const lessonConnectorId = getConnectorId(contentfulLesson)
  const lessonRoute = normalizeRoute(
    resolveLessonSlug({ contentfulLesson, lessonIndex }),
  )

  const matchingSummary = lessonSummaries.find(summary => {
    const summaryConnectorId = getConnectorId(summary)
    const summaryRoute = normalizeRoute(summary?.route)

    return (
      (!!lessonConnectorId && lessonConnectorId === summaryConnectorId) ||
      (!!lessonRoute && lessonRoute === summaryRoute)
    )
  })

  return matchingSummary || lessonSummaries[lessonListIndex] || null
}

const firstLessonCompleted = lessonChoices => {
  let lesson = null
  let lessonIndex = null
  lessonChoices.map((choice, index) => {
    if (!lesson && choice.doneAt) {
      lesson = choice
      lessonIndex = index
    } else if (
      choice.doneAt &&
      new Date(choice.doneAt) < new Date(lesson.doneAt)
    ) {
      lesson = choice
      lessonIndex = index
    }
  })
  return lessonIndex
}

const lessonWithMostRecentProgress = lessonChoices => {
  let lesson = null
  let lessonIndex = null
  lessonChoices.map((choice, index) => {
    if (!lesson && choice.updatedAt) {
      lesson = choice
      lessonIndex = index
    } else if (
      choice.updatedAt &&
      new Date(choice.updatedAt) > new Date(lesson.updatedAt)
    ) {
      lesson = choice
      lessonIndex = index
    }
  })
  return lessonIndex
}

export const getDefaultLessonABIndex = lessonChoices => {
  const hasProgress = lessonChoices.some(choice => choice.progress)
  const hasCompletion = lessonChoices.some(choice => choice.doneAt)

  if (hasProgress) {
    if (hasCompletion) {
      return firstLessonCompleted(lessonChoices)
    }
    return lessonWithMostRecentProgress(lessonChoices)
  }
}

export const getUnitResourceUrl = unitResource =>
  unitResource?.file?.url || unitResource?.file?.file?.url

export const findNextLessonIndex = ({ lessons, lessonStartingIndex }) => {
  const incompleteLesson = lessons.find(lesson => {
    if (!lesson.choices) return !lesson.is_done

    return !lesson.choices.some(abLesson => abLesson.is_done)
  })

  if (incompleteLesson) {
    return lessons.indexOf(incompleteLesson) + lessonStartingIndex
  }

  return lessonStartingIndex - 1 + lessons.length
}

export const resolveChoiceIndex = ({
  lessonChoices,
  selectedLessonAB,
  useDefaultLessonAB,
}) => {
  if (!useDefaultLessonAB && selectedLessonAB !== null) {
    return selectedLessonAB
  }

  return getDefaultLessonABIndex(lessonChoices)
}

export const getChoiceContentLesson = (choiceIndex, contentChoiceLesson) => {
  if (choiceIndex >= 0) {
    return choiceIndex === 0
      ? contentChoiceLesson.item1Lesson
      : contentChoiceLesson.item2Lesson
  }

  return contentChoiceLesson
}

export const buildLessonContent = ({ contentfulLesson, lessonSummary }) => {
  if (lessonSummary?.description) {
    return {
      ...contentfulLesson,
      description: lessonSummary.description,
    }
  }

  return contentfulLesson
}

export const isLessonOpen = ({
  aLessonIsOpen,
  index,
  lessonStartingIndex,
  selectedLessonIndex,
}) => {
  if (aLessonIsOpen) return false

  return selectedLessonIndex === lessonStartingIndex + index
}

export const buildLessonItems = ({
  aLessonIsOpen,
  contentfulLessons,
  lessonStartingIndex,
  lessonSummaries,
  lessons,
  selectedLessonAB,
  selectedLessonIndex,
  useDefaultLessonAB,
}) =>
  lessons.map((lesson, index) => {
    const lessonChoices = lesson.choices || null
    const lessonChoiceIndex = lessonChoices
      ? resolveChoiceIndex({
          lessonChoices,
          selectedLessonAB,
          useDefaultLessonAB,
        })
      : undefined
    const contentfulLesson = lessonChoices
      ? getChoiceContentLesson(lessonChoiceIndex, contentfulLessons[index])
      : contentfulLessons[index]
    const lessonIndex = lessonStartingIndex + index
    const lessonSummary = getLessonSummary({
      contentfulLesson,
      lessonIndex,
      lessonListIndex: index,
      lessonSummaries,
    })
    const lessonContent = buildLessonContent({
      contentfulLesson,
      lessonSummary,
    })
    const selectedChoice =
      lessonChoices && lessonChoiceIndex >= 0
        ? lessonChoices[lessonChoiceIndex]
        : undefined

    return {
      hasChoices: Boolean(lessonChoices),
      isLessonDone: resolveLessonDoneState({
        lesson: selectedChoice || lesson,
        lessonSummary,
      }),
      key: lessonChoices ? `lesson-ab-${lesson.scoID}` : lesson.scoID,
      lesson,
      lessonChoiceIndex,
      lessonContent,
      lessonIndex,
      listIndex: index,
      openThisLesson: isLessonOpen({
        aLessonIsOpen,
        index,
        lessonStartingIndex,
        selectedLessonIndex,
      }),
      selectedChoice,
    }
  })
